"use client"

import { useEffect, useState } from "react"
import { useToast } from "@/hooks/use-toast"
import { apiRequestWithAuth, getStoredToken, isTokenValid } from "@/lib/api-config"
import { useAuth } from "@/contexts/auth-context"
import { DashboardLayout } from "@/components/dashboard-layout"
import { Button } from "animal-island-ui"
import { CaretDown as ChevronDown } from "@phosphor-icons/react"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"

interface LogItem {
  id: number
  level: string
  taskType: string
  title: string
  detail: string
  imageUrl?: string
  from?: string | null
  server?: number
  name: string
  account: string
  password?: string
  time: string
  delete?: number
}

export default function UserLogs() {
  const { token: contextToken } = useAuth()
  const { toast } = useToast()
  const [logs, setLogs] = useState<LogItem[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const pageSize = 7
  const [total, setTotal] = useState(0)
  const [previewImg, setPreviewImg] = useState<string | null>(null)

  const getToken = () => contextToken || getStoredToken()

  const fetchLogs = async (pageNum = 1) => {
    const token = getToken()
    if (!token || !isTokenValid(token)) {
      toast({ variant: "destructive", title: "认证失败", description: "请重新登录" })
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const url = `/showMyLog?current=${pageNum}&size=${pageSize}`
      const result = await apiRequestWithAuth(url, token, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      })
      if (result.code === 200) {
        const data = result.data as { records: LogItem[]; total: number; current: number }
        setLogs(data.records || [])
        setTotal(data.total || 0)
      } else {
        toast({ variant: "destructive", title: "获取失败", description: result.msg })
      }
    } catch (error) {
      toast({ variant: "destructive", title: "网络错误", description: error instanceof Error ? error.message : "获取失败" })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchLogs(page)
  }, [page])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("zh-CN")
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <DashboardLayout>
      <div className="compact-page compact-logs">
        <div className="compact-toolbar">
          <span className="compact-muted">共 {total} 条 · 每页 {pageSize} 条</span>
          <Button type="default" loading={loading} onClick={() => fetchLogs(page)} disabled={loading}>刷新日志</Button>
        </div>
        <div className="compact-log-list" aria-busy={loading}>
          {loading ? <p role="status" className="compact-empty">加载中…</p> : logs.length === 0 ? <p className="compact-empty">暂无日志</p> : logs.map(log => (
            <details className="compact-log" key={log.id}>
              <summary>
                <span className="compact-log-text"><strong>{log.title}</strong><time>{formatDate(log.time)}</time></span>
                <span className={log.level === "WARN" || log.level === "ERROR" ? "log-level log-warning" : "log-level"}>{log.level}</span>
                <ChevronDown size={17} aria-hidden="true" />
              </summary>
              <div className="compact-log-detail">
                <p className="compact-muted">用户：{log.name} · 任务类型：{log.taskType}</p>
                <p>{log.detail}</p>
                {log.imageUrl && <button className="log-image-button" onClick={() => setPreviewImg(log.imageUrl!)} aria-label="查看日志大图"><img src={log.imageUrl} alt="日志图片" loading="lazy" /></button>}
              </div>
            </details>
          ))}
        </div>
        <nav className="compact-pagination" aria-label="日志分页">
          <Button type="default" size="small" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1 || loading}>上一页</Button>
          <span aria-live="polite">第 {page} / {totalPages} 页</span>
          <Button type="default" size="small" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading}>下一页</Button>
        </nav>
        <Dialog open={!!previewImg} onOpenChange={open => !open && setPreviewImg(null)}>
          <DialogContent aria-describedby={undefined} className="max-w-2xl">
            <DialogTitle className="sr-only">日志图片预览</DialogTitle>
            {previewImg && <img src={previewImg} alt="大图预览" className="max-h-[70vh] max-w-full rounded" />}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  )
}
